import { workerInfoType } from '../../components/singleInput/SingleInputMain'
import {
  Dirary,
  summaryByWorker,
  summaryByProductNo,
  processInfo,
} from '../../types/dirary'
import { constructionsForInput } from '../variables/constructions'
import { factories } from '../variables/factotries'
const getConstructionName = (constructionId: number) => {
  const construction = constructionsForInput.find(
    (c) => c.id === constructionId
  )
  return construction ? construction.name : ''
}

const getFactoryName = (factoryId: number) => {
  const factory = factories.find((f) => f.id === factoryId)
  return factory ? factory.name : ''
}

export const gatherDairyListAsWorkerAndProductNoAndSiteName = (
  diaryList: Dirary[]
) => {
  const aggregatedResult: {
    workerId: string
    workerName: string
    constructionId: number
    constructionName: string
    productNo: string
    productBno: string
    siteName: string
    factoryId: number
    factoryName: string
    totalWorkingTimeByMinute: number
    totalEstimatedTime: number
    totalWorkingTimeByHour: number
    totalWorkingTimeText: string
    achivementByProcess: processInfo[]
  }[] = []

  const aggregateMap = new Map<
    string,
    {
      workerId: string
      workerName: string
      constructionId: number
      constructionName: string
      productNo: string
      productBno: string
      siteName: string
      factoryId: number
      factoryName: string
      totalWorkingTimeByMinute: number
      totalEstimatedTime: number
      totalWorkingTimeByHour: number
      totalWorkingTimeText: string
      achivementByProcess: Map<string, processInfo>
      latestWorkingDate: string
    }
  >()

  diaryList.forEach((diary) => {
    // キーからsiteNameを除外
    const key = `${diary.workerId}-${diary.productNo}-${diary.productBno}-${diary.constructionId}-${diary.factoryId}`

    if (!aggregateMap.has(key)) {
      aggregateMap.set(key, {
        workerId: diary.workerId,
        workerName: diary.workerName,
        constructionId: diary.constructionId,
        constructionName: getConstructionName(diary.constructionId),
        productNo: diary.productNo,
        productBno: diary.productBno,
        siteName: diary.siteName,
        factoryId: diary.factoryId,
        factoryName: getFactoryName(diary.factoryId),
        totalWorkingTimeByMinute: 0,
        totalEstimatedTime: 0,
        totalWorkingTimeByHour: 0,
        totalWorkingTimeText: '',
        achivementByProcess: new Map<string, processInfo>(),
        latestWorkingDate: diary.workingDate,
      })
    }

    const aggregateEntry = aggregateMap.get(key)!
    aggregateEntry.totalWorkingTimeByMinute += diary.workingTimeByMinute
    aggregateEntry.totalEstimatedTime += diary.estimatedTime
    aggregateEntry.totalWorkingTimeByHour += diary.workingTimeByHour

    // 作業日が新しい場合は現場名を更新
    if (diary.workingDate > aggregateEntry.latestWorkingDate) {
      aggregateEntry.siteName = diary.siteName
      aggregateEntry.latestWorkingDate = diary.workingDate
    }

    // 各工程の合計を計算
    const processKey = diary.processId
    if (!aggregateEntry.achivementByProcess.has(processKey)) {
      aggregateEntry.achivementByProcess.set(processKey, {
        id: diary.id,
        processId: diary.processId,
        processName: diary.processName,
        productNo: diary.productNo,
        productBno: diary.productBno,
        slipNumber: diary.slipNumber,
        estimatedTime: 0,
        achievementsTimeByHour: 0,
        achievementsTimeByMinute: 0,
        laborClassification: 0,
      })
    }

    const processEntry = aggregateEntry.achivementByProcess.get(processKey)!
    processEntry.estimatedTime += diary.estimatedTime
    processEntry.achievementsTimeByHour += diary.workingTimeByHour
    processEntry.achievementsTimeByMinute += diary.workingTimeByMinute

    // totalWorkingTimeTextを計算
    const totalMinutes = aggregateEntry.totalWorkingTimeByMinute
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    aggregateEntry.totalWorkingTimeText = `${hours}時間${minutes}分`
  })

  aggregateMap.forEach((value) => {
    aggregatedResult.push({
      ...value,
      achivementByProcess: Array.from(value.achivementByProcess.values()),
    })
  })

  return aggregatedResult
}
export const gatherDairyListAsProductNoAndSiteName = (
  aggregatedData: summaryByWorker[]
) => {
  const finalAggregatedResult: {
    constructionId: number
    constructionName: string
    productNo: string
    productBno: string
    siteName: string
    factoryId: number
    factoryName: string
    totalWorkingTimeByMinute: number
    totalWorkingTimeByHour: number
    totalWorkingTimeText: string
    totalEstimatedTime: number
    numberOfWorkers: number
    achivementByProcess: processInfo[]
  }[] = []

  const finalAggregateMap = new Map<
    string,
    {
      constructionId: number
      constructionName: string
      productNo: string
      productBno: string
      siteName: string
      factoryId: number
      factoryName: string
      totalWorkingTimeByMinute: number
      totalWorkingTimeByHour: number
      totalWorkingTimeText: string
      totalEstimatedTime: number
      numberOfWorkers: Set<string>
      achivementByProcess: Map<string, processInfo>
    }
  >()

  aggregatedData.forEach((data) => {
    // キーからsiteNameを除外
    const key = `${data.constructionId}-${data.productNo}-${data.productBno}-${data.factoryId}`

    if (!finalAggregateMap.has(key)) {
      finalAggregateMap.set(key, {
        constructionId: data.constructionId,
        constructionName: getConstructionName(data.constructionId),
        productNo: data.productNo,
        productBno: data.productBno,
        siteName: data.siteName,
        factoryId: data.factoryId,
        factoryName: getFactoryName(data.factoryId),
        totalWorkingTimeByMinute: 0,
        totalWorkingTimeByHour: 0,
        totalWorkingTimeText: '',
        totalEstimatedTime: 0,
        numberOfWorkers: new Set<string>(),
        achivementByProcess: new Map<string, processInfo>(),
      })
    }

    const finalAggregateEntry = finalAggregateMap.get(key)!
    finalAggregateEntry.totalWorkingTimeByMinute +=
      data.totalWorkingTimeByMinute
    finalAggregateEntry.totalWorkingTimeByHour += data.totalWorkingTimeByHour
    finalAggregateEntry.totalEstimatedTime += data.totalEstimatedTime
    finalAggregateEntry.numberOfWorkers.add(data.workerId)

    // 各工程の合計を計算
    data.achivementByProcess.forEach((process) => {
      const processKey = process.processId
      if (!finalAggregateEntry.achivementByProcess.has(processKey)) {
        finalAggregateEntry.achivementByProcess.set(processKey, {
          id: process.id,
          processId: process.processId,
          processName: process.processName,
          productNo: process.productNo,
          productBno: process.productBno,
          slipNumber: process.slipNumber,
          estimatedTime: 0,
          achievementsTimeByHour: 0,
          achievementsTimeByMinute: 0,
          laborClassification: 0,
        })
      }

      const processEntry =
        finalAggregateEntry.achivementByProcess.get(processKey)!
      processEntry.estimatedTime += process.estimatedTime
      processEntry.achievementsTimeByHour += process.achievementsTimeByHour
      processEntry.achievementsTimeByMinute += process.achievementsTimeByMinute
    })

    // totalWorkingTimeTextを計算
    const totalMinutes = finalAggregateEntry.totalWorkingTimeByMinute
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    finalAggregateEntry.totalWorkingTimeText = `${hours}時間${minutes}分`
  })

  finalAggregateMap.forEach((value) => {
    finalAggregatedResult.push({
      ...value,
      numberOfWorkers: value.numberOfWorkers.size,
      achivementByProcess: Array.from(value.achivementByProcess.values()),
    })
  })

  return finalAggregatedResult
}

export const diaryListForTable = (
  diaryList: Dirary[],
  summary: summaryByWorker
) => {
  return diaryList.filter((diary) => {
    return (
      diary.workerId === summary.workerId &&
      diary.constructionId === summary.constructionId &&
      diary.productNo === summary.productNo &&
      diary.productBno === summary.productBno &&
      diary.factoryId === summary.factoryId
    )
  })
}

export const extractUniqueWorkers = (
  diraryArray: Dirary[]
): { id: string; name: string }[] => {
  const workerSet = new Set<string>()
  const uniqueWorkers: { id: string; name: string }[] = []

  for (const dirary of diraryArray) {
    const workerKey = `${dirary.workerId}-${dirary.workerName}`
    if (!workerSet.has(workerKey)) {
      workerSet.add(workerKey)
      uniqueWorkers.push({ id: dirary.workerId, name: dirary.workerName })
    }
  }

  return uniqueWorkers
}
